import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  deactivateTicketStatus,
  getApiErrorMessage,
  listTicketStatuses,
} from '../../api/client';
import type { TicketStatusDefinition } from '../../api/types';
import Badge from '../../components/Badge';
import { useAuth } from '../../contexts/AuthContext';
import { canEditPlatformConfig, canWritePlatformConfig } from '../../utils/permissions';
import { TICKET_STATUS_BEHAVIOR_LABELS } from '@21k/shared';
import { PLATFORM_CONFIG } from '../platform-config/paths';

export default function TicketStatusesPage() {
  const { user } = useAuth();
  const [statuses, setStatuses] = useState<TicketStatusDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const canCreate = user && canWritePlatformConfig(user);
  const canModify = user && canEditPlatformConfig(user);

  useEffect(() => {
    listTicketStatuses()
      .then(setStatuses)
      .catch((err) => setError(getApiErrorMessage(err)))
      .finally(() => setLoading(false));
  }, []);

  async function handleDeactivate(id: string, name: string) {
    if (
      !window.confirm(
        `Deactivate status "${name}"? Existing tickets keep it; new tickets cannot use it.`,
      )
    ) {
      return;
    }
    try {
      const updated = await deactivateTicketStatus(id);
      setStatuses((prev) => prev.map((row) => (row.id === id ? updated : row)));
    } catch (err) {
      setError(getApiErrorMessage(err));
    }
  }

  return (
    <>
      <div className="page-header" style={{ marginTop: 0 }}>
        <div>
          <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.9rem' }}>
            Define workflow states and allowed transitions between them.
          </p>
        </div>
        {canCreate && (
          <Link to={PLATFORM_CONFIG.statusesNew} className="btn btn-primary">
            Add status
          </Link>
        )}
      </div>
      {error && <div className="alert alert-error">{error}</div>}
      <div className="card">
        {loading ? (
          <div className="loading">Loading statuses…</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Behavior</th>
                <th>Transitions</th>
                <th>Tickets</th>
                <th>Status</th>
                {canModify && <th />}
              </tr>
            </thead>
            <tbody>
              {statuses.map((s) => (
                <tr key={s.id}>
                  <td>
                    {canModify ? <Link to={PLATFORM_CONFIG.status(s.id)}>{s.code}</Link> : s.code}
                    {s.isDefault && (
                      <>
                        {' '}
                        <Badge label="DEFAULT" className="badge-neutral" />
                      </>
                    )}
                  </td>
                  <td>{s.name}</td>
                  <td style={{ fontSize: '0.85rem' }}>
                    {TICKET_STATUS_BEHAVIOR_LABELS[s.onEnterBehavior]}
                    {s.isTerminal && ' · terminal'}
                  </td>
                  <td>{s.allowedTargetIds.length}</td>
                  <td>{s._count?.tickets ?? '—'}</td>
                  <td>
                    <Badge
                      label={s.isActive ? 'ACTIVE' : 'INACTIVE'}
                      className={s.isActive ? 'badge-success' : 'badge-neutral'}
                    />
                  </td>
                  {canModify && (
                    <td>
                      {s.isActive && (
                        <button
                          type="button"
                          className="btn btn-danger-ghost"
                          onClick={() => void handleDeactivate(s.id, s.name)}
                        >
                          Deactivate
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
